import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { 
  getAllPlans, 
  getPlanById, 
  getActivePlans, 
  createPlan, 
  updatePlan, 
  deletePlan, 
  togglePlanStatus,
  togglePlanPopular
} from '../../api/admin/adminPaymentPlandApi';

export const useAdminPlans = () => {
  const { setError } = useAuthStore();

  const {
    data: plans,
    isLoading,
    error,
    refetch
  } = useQuery('adminPlans', getAllPlans, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch plans';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    plans: plans?.data || [],
    isLoading,
    error,
    refetchPlans: refetch
  };
};

export const useAdminPlan = (planId) => {
  const { setError } = useAuthStore();

  const {
    data: plan,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['adminPlan', planId],
    () => getPlanById(planId),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch plan details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: planId !== undefined && planId !== null,
    }
  );

  return {
    plan: plan?.data,
    isLoading,
    error,
    refetchPlan: refetch
  };
};

export const useAdminActivePlans = () => {
  const { setError } = useAuthStore();

  const {
    data: plans,
    isLoading,
    error,
    refetch
  } = useQuery('adminActivePlans', getActivePlans, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch active plans';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000,
    cacheTime: 3600000,
  });

  return {
    plans: plans?.data || [],
    isLoading,
    error,
    refetchPlans: refetch
  };
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(createPlan, {
    onSuccess: (data) => {
      toast.success('Plan created successfully!');
      queryClient.invalidateQueries('adminPlans');
      queryClient.invalidateQueries('adminActivePlans');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create plan';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(({ id, planData }) => updatePlan(id, planData), {
    onSuccess: (data, variables) => {
      toast.success('Plan updated successfully!');
      queryClient.invalidateQueries('adminPlans');
      queryClient.invalidateQueries('adminActivePlans');
      queryClient.invalidateQueries(['adminPlan', variables.id]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update plan';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useDeletePlan = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(deletePlan, {
    onSuccess: (data) => {
      toast.success('Plan deleted successfully!');
      queryClient.invalidateQueries('adminPlans');
      queryClient.invalidateQueries('adminActivePlans');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete plan';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useTogglePlanStatus = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(togglePlanStatus, {
    onSuccess: (data) => {
      const isActive = data.data.is_active;
      toast.success(`Plan ${isActive ? 'activated' : 'deactivated'} successfully!`);
      queryClient.invalidateQueries('adminPlans');
      queryClient.invalidateQueries('adminActivePlans');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to toggle plan status';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useTogglePlanPopular = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(togglePlanPopular, {
    onSuccess: (data) => {
      const isPopular = data.data.popular;
      toast.success(`Plan ${isPopular ? 'marked as popular' : 'unmarked as popular'} successfully!`);
      queryClient.invalidateQueries('adminPlans');
      queryClient.invalidateQueries('adminActivePlans');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to toggle plan popular status';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};